import {
  useQuery,
  UseQueryOptions,
  QueryKey,
  useMutation,
  useQueryClient,
  UseMutationOptions,
} from "@tanstack/react-query";

export async function generateEvaluationScript(id: string): Promise<any> {
  const url = `${getBITSEndApiMap("getProjectById")}/${id}/evaluation-script/generate`;
  const { data } = await api.post(url);
  return data;
}

export async function updateEvaluationScript(id: string, scriptContent: string): Promise<any> {
  const url = `${getBITSEndApiMap("getProjectById")}/${id}/evaluation-script`;
  const { data } = await api.put(url, { scriptContent });
  return data;
}

export function useUpdateEvaluationScript() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, scriptContent }: { id: string; scriptContent: string }) => 
      updateEvaluationScript(id, scriptContent),
    onSuccess: (_, { id }) => {
      toast.success("Script updated and saved successfully!");
      queryClient.invalidateQueries({ queryKey: ["evaluation-script", id] });
    },
    onError: (error: any) => {
      console.error("Failed to save test script manually", error);
      toast.error(error?.response?.data?.message || "Failed to update AI Evaluation Script");
    },
  });
}

export function useGenerateEvaluationScript() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => generateEvaluationScript(id),
    onSuccess: (_, id) => {
      toast.success("AI Script generation started. Check back soon!");
      queryClient.invalidateQueries({ queryKey: ["project", id] });
      queryClient.invalidateQueries({ queryKey: ["evaluation-script-status", id] });
    },
    onError: (error: any) => {
      console.error("Failed to generate test script", error);
      toast.error(error?.response?.data?.message || "Failed to trigger AI generation");
    },
  });
}

export async function getEvaluationScript(id: string): Promise<string> {
  const url = `${getBITSEndApiMap("getProjectById")}/${id}/evaluation-script`;
  const { data } = await api.get(url);
  // The backend controller for getEvaluationScript returns the script heavily wrapped by buildSuccessResponse
  return data?.data?.scriptContent || "";
}

export function useGetEvaluationScriptQuery(id: string, options?: any) {
  return useQuery({
    queryKey: ["evaluation-script", id],
    queryFn: () => getEvaluationScript(id),
    enabled: !!id,
    ...options
  });
}

export interface EvaluationScriptStatusResponse {
  success: boolean;
  status: 'idle' | 'generating' | 'completed' | 'failed' | 'empty';
  message: string | null;
}

export async function getEvaluationScriptStatus(id: string): Promise<EvaluationScriptStatusResponse> {
  const url = `${getBITSEndApiMap("getProjectById")}/${id}/evaluation-script/status`;
  const { data } = await api.get(url);
  return data;
}

export function useEvaluationScriptStatusQuery(id: string, options?: any) {
  return useQuery<EvaluationScriptStatusResponse, Error>({
    queryKey: ["evaluation-script-status", id],
    queryFn: () => getEvaluationScriptStatus(id),
    enabled: !!id,
    ...options
  });
}

import getBITSEndApiMap from "../apiMap";
import { api } from "../services/AxiosService";
import { toast } from "sonner";
export interface Instructor {
  id: string;
  name: string;
  email: string;
}

export interface UserSubmission {
  id: string;
  status: "submitted" | "pending" | "graded" | string;
  submittedAt?: string | null;
  grade?: number | null;
  feedback?: string | null;
}

export interface EnhancedProjectCourse {
  id: string;
  name: string;
  code: string;
  term: string;
  description?: string;
  instructor: Instructor;
}

export interface ResourceProfile {
  id: string;
  name: string;
  resource_type: string;
  cpu_cores: number;
  ram_gb: number;
  disk_gb: number;
  packages?: string[];
  custom_packages?: string[];
  build_status: string;
}

export interface EnhancedProject {
  id: string;
  projectId?: string; // optional field for backward compatibility
  title: string;
  template_name?: string; // for template data
  template_description?: string; // for template data
  description?: string;
  status: "draft" | "published" | "archived" | string;
  difficultyLevel: "beginner" | "intermediate" | "advanced" | string;
  start_date: string | null;
  due_date: string | null;
  submissionCount: number;
  userSubmission: UserSubmission | null;
  instructors: Instructor[];
  course: EnhancedProjectCourse;
  checkpoints: any[];
  subject?: string;
  difficulty?: string;
  progress?: any;
  sandboxStatus?: string;
  totalPoints?: any;
  hasDataset?: any;
  totalCheckpoints?: any;
  createdAt?: string;
  updatedAt?: string;
  isLinked?: boolean; // Add isLinked property to track if project is linked to LTI

  // 🔽 Same names as API response
  instructions?: string | null;
  course_id?: string;
  created_by?: string;
  difficulty_level?: string;
  estimated_hours?: number;
  notebook_template_s3_url?: string | null;
  dataset_s3_url?: any[];
  additional_files_s3_urls?: any[];
  late_submission_allowed?: boolean;
  late_penalty_percent?: string;
  max_attempts?: number;
  auto_grading_enabled?: boolean;
  learning_objectives?: string;
  prerequisites?: string;
  tags?: string[];
  settings?: Record<string, any>;
  isScreen?: number;
  project_code?: string;
  category_id?: string;
  instructor_id?: string[];
  teaching_ass_id?: string[];
  total_points?: number;
  project_overview?: string;
  type?: string;
  sandbox_time_duration?: string | null;
  late_submission_days_allowed?: number;
  closed_project?: boolean;
  deletedAt?: string | null;
  creator?: {
    id: string;
    name: string;
    email: string;
  };
  rubrics?: any[];
  template?: any;
  assignments?: any[];
  assignmentStats?: {
    totalAssignments: number;
    byRole: Record<string, any>;
    roles: string[];
  };
  teachingAssistants?: {
    id: string;
    name: string;
    email: string;
    status: string;
  }[];
  students?: {
    id: string;
    name: string;
    email: string;
    status: string;
  }[];
  Submission?: {
    count: number;
    rows: any[];
  };
  is_group_project?: boolean;
  linkedDatasets?: Array<{
    id: string;
    title: string;
    description?: string;
    s3Keys?: any[];
    visibility?: string;
    tags?: string[];
    coverPhotoS3Key?: string;
    createdAt?: string;
    customInstructions?: string;
    linkedAt?: string;
  }>;
  linkedDatasetsCount?: number;
  datasets?: Array<{
    id: string;
    title: string;
    description?: string;
    s3Keys?: any[];
    visibility?: string;
    tags?: string[];
    coverPhotoS3Key?: string;
    createdAt?: string;
    linkInfo?: {
      customInstructions?: string;
      createdAt?: string;
    };
  }>;
  resourceProfile?: ResourceProfile | string;
  userRole?: string;
}

export interface EnhancedProjectsPagination {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
}

export interface EnhancedProjectsResponseData {
  projects: EnhancedProject[];
  pagination: EnhancedProjectsPagination;
}

export interface EnhancedProjectsResponse {
  isSuccess: boolean;
  message: string;
  data: EnhancedProjectsResponseData;
}

export interface GetEnhancedProjectsParams {
  page?: number; // default 1
  limit?: number; // default 10
  search?: string;
  courseId?: string;
  status?: "draft" | "published" | "archived";
  difficultyLevel?: "beginner" | "intermediate" | "advanced";
  isLinked?: boolean; // Add isLinked parameter for filtering linked/unlinked projects
  type?: "group" | "individual"; // Add type parameter for filtering group/individual projects
  token?: string | null; // Add token parameter (can be null)
}
export interface studentExport {
  id?: string;
  isExport?: boolean;
}

export async function getEnhancedProjects(
  params: GetEnhancedProjectsParams = {},
): Promise<EnhancedProjectsResponseData> {
  const url = getBITSEndApiMap("getProjects");
  // Filter out token from params since it's handled by the request interceptor
  const { token, ...apiParams } = params;
  const { data } = await api.get<EnhancedProjectsResponse>(url, {
    params: apiParams,
  });
  return data.data;
}

export async function publishProject(
  projectId: string,
): Promise<{ success: boolean; message: string }> {
  const url = getBITSEndApiMap("publishProject").replace(":id", projectId);
  const { data } = await api.put(url);
  return data;
}

export async function unpublishProject(
  projectId: string,
): Promise<{ success: boolean; message: string }> {
  const url = getBITSEndApiMap("unpublishProject").replace(":id", projectId);
  const { data } = await api.put(url);
  return data;
}
export async function getProjectById(id: string): Promise<EnhancedProject> {
  const url = `${getBITSEndApiMap("getProjectById")}/${id}/details`;
  const response = await api.get(url);
  const payload = response.data as any;

  // Handle both legacy { data: { data } } and current { data } API shapes
  const projectData = payload?.data?.data ?? payload?.data ?? payload;

  if (!projectData || typeof projectData !== "object") {
    throw new Error("Project data missing from response");
  }

  return projectData as EnhancedProject;
}
export async function getProjectByIdExport(
  params: studentExport,
): Promise<Blob> {
  const { id, isExport } = params;
  const queryParams = new URLSearchParams();
  if (isExport) queryParams.append("isExport", "true");
  const url = `${getBITSEndApiMap(
    "getProjectById",
  )}/${id}/details?${queryParams.toString()}`;
  const { data } = await api.get<Blob>(url, {
    responseType: "blob",
  });
  return data;
}
export async function getInstructorDashboardData(): Promise<InstructorDashboardData> {
  const url = `${getBITSEndApiMap("instructorDashboard")}?page=1&limit=10`;
  const { data } = await api.get<{ data: InstructorDashboardData }>(url);
  return data.data;
}
export async function getTemplateById(id: string): Promise<EnhancedProject> {
  const url = `${getBITSEndApiMap("getProjectById")}/templates/${id}`;
  const { data } = await api.get<{ data: EnhancedProject }>(url);
  return data.data;
}

export async function deleteProject(id: string): Promise<void> {
  const url = `${getBITSEndApiMap("deleteProject")}/${id}`;
  await api.delete(url);
}
export function useDeleteProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteProject(id),
    onSuccess: () => {
      // ✅ invalidate the projects list query so it refetches
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
    onError: (error) => {
      console.error("Delete failed:", error);
    },
  });
}
export async function closeProject(id: string) {
  const url = `${getBITSEndApiMap("deleteProject")}/${id}/close`;
  const response = await api.delete(url);
  return response;
}

export function useCloseProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => closeProject(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
    onError: (error) => {
      console.error("Close failed:", error);
    },
  });
}

export async function duplicateProject(id: string): Promise<any> {
  const url = `${getBITSEndApiMap("duplicateProjectByID")}${id}/duplicate`;
  const { data } = await api.post(url);
  return data;
}

export function useDuplicateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => duplicateProject(id),
    onSuccess: (_, id) => {
      toast.success(`Project duplicated successfully`);
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
    onError: () => {
      toast.error("Failed to duplicate project");
    },
  });
}

export async function createTemplateFromProject(
  projectId: string,
  templateData: {
    title: string;
    description: string;
    category: string;
    subcategory: string;
    skills_covered: string[];
    technologies_used: string[];
  },
): Promise<any> {
  const url = `${getBITSEndApiMap(
    "duplicateProjectByID",
  )}${projectId}/create-template`;
  const { data } = await api.post(url, templateData);
  return data;
}

export function useCreateTemplateFromProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      templateData,
    }: {
      projectId: string;
      templateData: {
        title: string;
        description: string;
        category: string;
        subcategory: string;
        skills_covered: string[];
        technologies_used: string[];
      };
    }) => createTemplateFromProject(projectId, templateData),
    onSuccess: (data, { projectId }) => {
      toast.success("Template created from project successfully");

      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["templates"] });
      queryClient.invalidateQueries({ queryKey: ["project", projectId] });
    },
    onError: (error: any) => {
      console.log(error);
      const errorMessage =
        error?.response?.data?.data?.message ||
        "Failed to create template from project";
      toast.error(errorMessage);
    },
  });
}
export function useEnhancedProjectsQuery(
  params: GetEnhancedProjectsParams,
  options?: Partial<
    UseQueryOptions<
      EnhancedProjectsResponseData,
      Error,
      EnhancedProjectsResponseData,
      QueryKey
    >
  >,
) {
  return useQuery<
    EnhancedProjectsResponseData,
    Error,
    EnhancedProjectsResponseData,
    QueryKey
  >({
    queryKey: ["projects", "enhanced", params] as QueryKey,
    queryFn: () => getEnhancedProjects(params),
    ...(options as any),
  });
}

// Types
export interface GetTemplatesParams {
  page?: number;
  limit?: number;
  category?: string;
  subcategory?: string;
  difficultyLevel?: "beginner" | "intermediate" | "advanced";
  isFeatured?: boolean;
  isPublic?: boolean;
  search?: string;
  token?: string; // will be stripped out before request
}
interface ProjectStats {
  totalProjectsCount: number;
  totalActiveProjectsCount: number;
  totalTemplateProjectsCount: number;
  totalCompletedProjectsCount: number;
}

export interface ProjectFilesData {
  success: boolean;
  count: number;
  prefix: string;
  data: string[];
}

export interface ProjectFilesResponse {
  success: boolean;
  data: ProjectFilesData;
}

export interface GroupMember {
  userId: string;
  name: string;
  email: string;
  role: "leader" | "member" | string;
  isLeader: boolean;
  isCurrentUser: boolean;
}

export interface GroupDetailsResponse {
  isSuccess: boolean;
  message: string;
  data: GroupMember[];
}

export interface ShareFilePayload {
  filename?: string | string[];
  filenames?: string[];
  recipientIds: string[];
  message?: string;
}

export interface ShareFileResponse {
  isSuccess: boolean;
  message: string;
  data?: any;
}

export interface SharedFileRecipient {
  userId: string;
  name: string;
  email: string;
  status: "success" | "failed";
  destinationFilename: string;
  reason: string | null;
}

export interface SharedFileResult {
  filename: string;
  prefixedFilename: string;
  shares: SharedFileRecipient[];
  status: "processed" | "not_found";
}

export interface SharedFilesSummary {
  totalFiles: number;
  filesProcessed: number;
  filesNotFound: number;
  totalSharesAttempted: number;
  successfulShares: number;
  failedShares: number;
}

export interface SharedFilesData {
  sharedBy: string;
  timestamp: string;
  summary: SharedFilesSummary;
  results: SharedFileResult[];
}

export interface SharedFilesResponse {
  isSuccess: boolean;
  message: string;
  data: SharedFilesData;
}

export interface InstructorDashboardData {
  totalProjects: number;
  activeProjects: number;
  totalStudents: number;
  pendingGrades: number;
  averageGrade: number;
}

// Shared files list for GET /files/shared
export interface SharedFileSender {
  id: string;
  name: string;
  email: string;
  profilePicture: string | null;
}

export interface SharedFileItem {
  id: string;
  fileName: string;
  originalFileName: string;
  message: string;
  sender: SharedFileSender;
}

export interface SharedFileListResponse {
  isSuccess: boolean;
  message: string;
  data: SharedFileItem[];
}
export interface TemplateItem {
  id: string;
  title: string;
  description: string;
  templateCategory: string;
  templateSubcategory: string;
  difficultyLevel: "beginner" | "intermediate" | "advanced";
  isFeatured: boolean;
  isPublic: boolean;
  tags?: string[];
  createdAt: string;
  updatedAt: string;
  [key: string]: any; // for extra fields
}

export interface TemplatesResponseData {
  items: TemplateItem[];
  total: number;
  page: number;
  limit: number;
}

export interface TemplatesResponse {
  status: string;
  message: string;
  data: TemplatesResponseData;
}

// API function
export async function getProjectTemplates(
  params: GetTemplatesParams = {},
): Promise<TemplatesResponseData> {
  const url = getBITSEndApiMap("getTemplates");
  const { token, ...apiParams } = params;
  const { data } = await api.get<TemplatesResponse>(url, { params: apiParams });
  return data.data;
}

// React Query hook
export function useProjectTemplatesQuery(
  params: GetTemplatesParams,
  options?: Partial<
    UseQueryOptions<
      TemplatesResponseData,
      Error,
      TemplatesResponseData,
      QueryKey
    >
  >,
) {
  return useQuery<
    TemplatesResponseData,
    Error,
    TemplatesResponseData,
    QueryKey
  >({
    queryKey: ["projects", "templates", params] as QueryKey,
    queryFn: () => getProjectTemplates(params),
    ...(options as any),
  });
}

interface DuplicateTemplateResponse {
  id: string;
  template_name: string;
  message?: string;
  [key: string]: any;
}

export async function duplicateProjectTemplate(
  id: string,
): Promise<DuplicateTemplateResponse> {
  const url = `${getBITSEndApiMap("getTemplates")}/${id}/duplicate`;
  const { data } = await api.post<DuplicateTemplateResponse>(url);
  return data;
}

export function useDuplicateProjectTemplate(
  options?: UseMutationOptions<DuplicateTemplateResponse, Error, string>,
) {
  return useMutation<DuplicateTemplateResponse, Error, string>({
    mutationFn: (id: string) => duplicateProjectTemplate(id),
    ...(options as any),
  });
}
// API call
export async function deleteProjectTemplate(id: string): Promise<void> {
  const url = `${getBITSEndApiMap("getTemplates")}/${id}`;
  await api.delete(url);
}

export function useDeleteProjectTemplateMutation(options?: Partial<any>) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (id: string) => deleteProjectTemplate(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["projects", "templates"] });

      queryClient.setQueryData<any>(["projects", "templates"], (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          templates: oldData.templates.filter((t: any) => t.id !== id),
        };
      });

      options?.onSuccess?.(_, id);
    },
    onError: (err, id, ctx) => {
      console.error("Delete failed:", err);
      options?.onError?.(err, id, ctx);
    },
  });
}
// export async function getTemplateById(id: string): Promise<EnhancedProject> {
//   const url = `${getBITSEndApiMap("getTemplateById")}/${id}`;
//   const { data } = await api.get<{ data: EnhancedProject }>(url);
//   return data.data;
// }

export function useGetTemplateByIdQuery(id: string) {
  return useQuery({
    queryKey: ["template", id],
    queryFn: () => getTemplateById(id),
    enabled: !!id, // Only run query if id exists
  });
}

export async function getProjectStats(): Promise<ProjectStats> {
  const url = getBITSEndApiMap("getProjectStats");
  const { data } = await api.get<{ data: ProjectStats }>(url, {});
  return data.data;
}

export async function getProjectFiles(
  projectId?: string,
): Promise<ProjectFilesData> {
  const baseUrl = getBITSEndApiMap("getProjectFiles");
  const url = projectId ? baseUrl.replace(":projectId", projectId) : baseUrl;
  const { data } = await api.get<ProjectFilesResponse>(url, {});
  return data.data;
}

export async function getGroupDetails(
  projectId?: string,
): Promise<GroupMember[]> {
  const baseUrl = getBITSEndApiMap("getGroupDetails");
  const url = projectId ? baseUrl.replace(":projectId", projectId) : baseUrl;
  const { data } = await api.get<GroupDetailsResponse>(url, {});
  return data.data;
}

export async function shareFile(
  projectId: string,
  payload: ShareFilePayload,
): Promise<ShareFileResponse> {
  const baseUrl = getBITSEndApiMap("shareFile");
  const url = baseUrl.replace(":projectId", projectId);
  const { data } = await api.post<ShareFileResponse>(url, payload);
  return data;
}

// Get list of files shared with the current user for a project
export async function getSharedFiles(
  projectId: string,
): Promise<SharedFileItem[]> {
  const baseUrl = getBITSEndApiMap("getSharedFile");
  const url = baseUrl.replace(":projectId", projectId);
  const { data } = await api.get<SharedFileListResponse>(url);
  return data.data;
}
